import {Draft} from 'immer';
import {Edge} from 'reactflow';

import {assert} from 'Asserts';
import type {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {isNewEdgeValidForFlowchart} from 'processes/modals/utils/TransitionModalValidations';
import {FlowchartChanges} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

import {getFieldInfoFromDataFlowEdgeHandle} from '../flowchartIdentifierUtils';
import {FlowchartStaticEdgeData, ProcessFlowchartEdgeType} from '../processFlowchartConfigEditorConstants';
import {StaticFlowchartNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {
  DeletedNodeContext,
  FlowchartEdgeChange,
  FlowchartEdgeChangeType,
} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {getFieldPairKey, getPTDVPairKey} from '../types/transition/transitionKeyUtils';
import {
  createSingleTransitionsForDisconnectedMergeByRemovedEdge as createSingleTransitionsForDisconnectedMerge,
  createTransitionsForNewControlFlowEdge,
  findOrphanedTransitionAndSourcePTDVIds,
  findSubsetTransitionsPromotedByNewEdge as findPromotedSubsetTransitions,
  getFieldPairKeyFromDataFlowEdge,
  getOptionalTransitionsForNewEdge,
  getPTDVIdsFromEdge,
  removeFieldPairForDataFlowEdge,
  removeSourcePTDVFromTransition,
} from '../types/transition/transitionLifecycleUtils';
import {findTaskSchemaNodePair} from '../types/transition/transitionLookupUtils';
import {processEdgeChangesWithMirroring} from './edgeMirroringUtils';
import {getEmptyFlowchartChanges} from './flowchartChangesUtils';
import getNodeChangesForNewEdge from './getNodeChangesForNewEdge';
import getRouterConfigChangesAfterEdgeChange from './getRouterConfigChangesAfterEdgeChange';

/**
 * Validates edge changes and filters out invalid changes.
 */
function validateEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  existingEdges: Array<FlowchartStaticEdgeData>
): Array<FlowchartEdgeChange> {
  return edgeChanges.filter((edgeChange) => {
    if (edgeChange.type !== FlowchartEdgeChangeType.ADD || !edgeChange.item) {
      return true;
    }
    return isNewEdgeValidForFlowchart(edgeChange.item, existingEdges);
  });
}

function applyAddEdgeChange(
  edgeItem: Edge,
  draft: Draft<FlowchartConfigWithMetadata>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  sideEffects: FlowchartChanges,
  options: ApplyFlowchartEdgeChangesOptions
): void {
  const {disallowTransitionEdits} = options;
  assert(edgeItem.sourceHandle != null, 'Source handle is required');
  assert(edgeItem.targetHandle != null, 'Target handle is required');
  assert(edgeItem.type != null, 'Type is required');
  const newEdge: FlowchartStaticEdgeData = {
    id: edgeItem.id,
    source: edgeItem.source,
    target: edgeItem.target,
    sourceHandle: edgeItem.sourceHandle,
    targetHandle: edgeItem.targetHandle,
    type: edgeItem.type as ProcessFlowchartEdgeType,
  };
  draft.staticFlowchart.edgesStaticData.push(newEdge);

  sideEffects.nodeChanges.push(
    ...getNodeChangesForNewEdge(draft.staticFlowchart, newEdge, draft.metadata.rootNodeStaticData)
  );

  // In ACM/PCM (controlled by the DISALLOW_TRANSITION_EDITS feature), users may only
  // draw edges representing pre-existing transitions. Skip the transition-creation and
  // optional→planned promotion side effects so we don't introduce transitions that
  // weren't already configured for the flowchart. The same flag also suppresses the
  // transition-mutating side effects in `applyRemoveEdgeChange` below.
  if (!disallowTransitionEdits) {
    const newTransitions = createTransitionsForNewControlFlowEdge(
      newEdge,
      draft.staticFlowchart.nodesStaticData,
      draft.staticFlowchart.edgesStaticData,
      draft.metadata.transitions
    );
    for (const transition of newTransitions) {
      draft.metadata.transitions[transition.id] = transition;
    }
  }
  if (newEdge.type === ProcessFlowchartEdgeType.CONTROL_FLOW && !disallowTransitionEdits) {
    for (const transition of getOptionalTransitionsForNewEdge(
      newEdge,
      draft.staticFlowchart.nodesStaticData,
      draft.staticFlowchart.edgesStaticData,
      draft.metadata.transitions
    )) {
      sideEffects.transitionChanges.push({type: 'update', id: transition.id, changes: {isPlanned: true}});
    }

    // When a new edge completes a merge, redundant subset transitions (single-source arms
    // and smaller sub-merges fully covered by the merge) are deleted.
    for (const id of findPromotedSubsetTransitions({
      newEdge,
      transitions: draft.metadata.transitions,
      nodes: draft.staticFlowchart.nodesStaticData,
      edges: draft.staticFlowchart.edgesStaticData,
    })) {
      delete draft.metadata.transitions[id];
    }
  }

  if (newEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
    const nodePair = findTaskSchemaNodePair(newEdge, draft.staticFlowchart.nodesStaticData);
    if (nodePair) {
      const sourcePTDVId = nodePair.sourceNode.taskDefinitionVersionSpec.clientId;
      const targetPTDVId = nodePair.targetNode.taskDefinitionVersionSpec.clientId;
      const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
      // Only mutate planned transitions here. Optional transitions represent
      // ACM/PCM configuration that's independent of the main canvas, so canvas
      // DF edge add/remove should not touch their field pair mappings.
      const transitionId = (ptdvPairToTransitionIdIndex[pairKey] ?? []).find(
        (id) => draft.metadata.transitions[id]?.isPlanned
      );

      if (transitionId != null) {
        const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(newEdge.sourceHandle);
        const targetHandle = getFieldInfoFromDataFlowEdgeHandle(newEdge.targetHandle);
        const fieldPairKey = getFieldPairKey({
          sourcePTDVId,
          sourceSectionType: sourceHandle.sectionType,
          sourceFieldClientId: sourceHandle.fieldClientId,
          targetPTDVId,
          targetSectionType: targetHandle.sectionType,
          targetFieldClientId: targetHandle.fieldClientId,
        });
        // Only initialize the field pair entry if it doesn't already exist in the
        // transition. This preserves custom mapping lookups when data flow edges are
        // re-created via mirroring.
        const existingTransition = draft.metadata.transitions[transitionId];
        if (
          existingTransition == null ||
          !existingTransition.customMappingDataByFieldPair.hasOwnProperty(fieldPairKey)
        ) {
          sideEffects.transitionChanges.push({
            type: 'update',
            id: transitionId,
            changes: {customMapping: {fieldPairKey, value: null}},
          });
        }
      }
    }
  }
}

/**
 * Checks whether any remaining data flow edge in the flowchart connects the same
 * PTDV pair with the same source/target field handles as the given removed edge.
 */
function hasRemainingDataFlowEdgeForFieldPair(
  removedEdge: FlowchartStaticEdgeData,
  removedEdgePtdvIds: {sourcePTDVId: string; targetPTDVId: string},
  staticFlowchart: {
    edgesStaticData: Array<FlowchartStaticEdgeData>;
    nodesStaticData: Array<StaticFlowchartNodeData>;
  }
): boolean {
  const removedFieldPairKey = getFieldPairKeyFromDataFlowEdge(removedEdge, staticFlowchart.nodesStaticData, {
    deletedSourceNodePTDVId: removedEdgePtdvIds.sourcePTDVId,
    deletedTargetNodePTDVId: removedEdgePtdvIds.targetPTDVId,
  });
  if (removedFieldPairKey == null) {
    return false;
  }

  return staticFlowchart.edgesStaticData.some(
    (edge) => getFieldPairKeyFromDataFlowEdge(edge, staticFlowchart.nodesStaticData) === removedFieldPairKey
  );
}

function cleanupOrphanedTransitions(
  removedEdge: FlowchartStaticEdgeData,
  draft: Draft<FlowchartConfigWithMetadata>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  deletedNodeContext?: DeletedNodeContext
): void {
  const {orphanedTransitionIds, orphanedSourcePTDV} = findOrphanedTransitionAndSourcePTDVIds(
    removedEdge,
    draft.staticFlowchart.edgesStaticData,
    draft.staticFlowchart.nodesStaticData,
    ptdvPairToTransitionIdIndex,
    draft.metadata.transitions,
    deletedNodeContext
  );
  for (const orphanedId of orphanedTransitionIds) {
    delete draft.metadata.transitions[orphanedId];
  }
  for (const {transitionId, sourcePTDVId} of orphanedSourcePTDV) {
    const transition = draft.metadata.transitions[transitionId];
    removeSourcePTDVFromTransition(draft.metadata.transitions, transitionId, sourcePTDVId);
    if (transition != null && transition.id !== transitionId) {
      // Transition was re-keyed — update the stale index so subsequent
      // edge removals in the same batch can still find it.
      for (const remainingSourceId of transition.sourcePTDVIds) {
        const pairKey = getPTDVPairKey(remainingSourceId, transition.targetPTDVId);
        const ids = ptdvPairToTransitionIdIndex[pairKey];
        if (ids != null) {
          const idx = ids.indexOf(transitionId);
          if (idx !== -1) {
            ids[idx] = transition.id;
          }
        }
      }
    }
  }
}

/**
 * When a data flow edge is removed, clears the corresponding field pair mapping
 * from planned transitions — but only if no other DF edge connects the same
 * field pair between the same PTDV pair.
 */
function cleanupDataFlowFieldPairMappings(
  removedEdge: FlowchartStaticEdgeData,
  draft: Draft<FlowchartConfigWithMetadata>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  deletedNodeContext?: DeletedNodeContext
): void {
  const ptdvIds = getPTDVIdsFromEdge(removedEdge, draft.staticFlowchart.nodesStaticData, deletedNodeContext);
  if (ptdvIds == null) {
    return;
  }
  if (hasRemainingDataFlowEdgeForFieldPair(removedEdge, ptdvIds, draft.staticFlowchart)) {
    return;
  }
  const pairKey = getPTDVPairKey(ptdvIds.sourcePTDVId, ptdvIds.targetPTDVId);
  for (const transitionId of ptdvPairToTransitionIdIndex[pairKey] ?? []) {
    const transition = draft.metadata.transitions[transitionId];
    // Optional transitions represent ACM/PCM configuration independent of the
    // main canvas, so canvas DF edge removal must not clear their field pair
    // mappings.
    if (transition != null && transition.isPlanned) {
      const updatedCustomMappingData = removeFieldPairForDataFlowEdge(
        removedEdge,
        draft.staticFlowchart.nodesStaticData,
        transition,
        deletedNodeContext
      );
      if (updatedCustomMappingData != null) {
        transition.customMappingDataByFieldPair = updatedCustomMappingData;
      }
    }
  }
}

function applyRemoveEdgeChange(
  edgeId: string,
  draft: Draft<FlowchartConfigWithMetadata>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  sideEffects: FlowchartChanges,
  options: ApplyFlowchartEdgeChangesOptions,
  deletedNodeContext?: DeletedNodeContext
): void {
  const {disallowTransitionEdits} = options;
  const removedEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === edgeId);
  draft.staticFlowchart.edgesStaticData = draft.staticFlowchart.edgesStaticData.filter(
    (edge) => edge.id !== edgeId
  );
  draft.metadata.interactionState.selectedEdgeIds.delete(edgeId);

  const routerChanges = getRouterConfigChangesAfterEdgeChange(
    draft.staticFlowchart,
    FlowchartEdgeChangeType.REMOVE,
    edgeId
  );
  sideEffects.nodeChanges.push(...routerChanges.nodeChanges);
  sideEffects.edgeChanges.push(...routerChanges.edgeChanges);

  // When transition edits are disallowed (ACM/PCM, execution viewer), removing an edge
  // must not delete pre-configured transitions, mutate their custom-mapping data, or
  // synthesize new single transitions from a partially-disconnected merge — only the
  // edge itself and the router config (which is structural, not a transition) change.
  if (removedEdge != null && !disallowTransitionEdits) {
    cleanupOrphanedTransitions(removedEdge, draft, ptdvPairToTransitionIdIndex, deletedNodeContext);

    if (removedEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      cleanupDataFlowFieldPairMappings(removedEdge, draft, ptdvPairToTransitionIdIndex, deletedNodeContext);
    }

    // If a merge transition has been disconnected, create new single transitions to replace the remaining edges.
    for (const newTransition of createSingleTransitionsForDisconnectedMerge({
      removedEdge,
      transitions: draft.metadata.transitions,
      nodes: draft.staticFlowchart.nodesStaticData,
      edges: draft.staticFlowchart.edgesStaticData,
    })) {
      draft.metadata.transitions[newTransition.id] = newTransition;
    }
  }
}

interface ApplyFlowchartEdgeChangesOptions {
  disallowTransitionEdits: boolean;
  editorMode: FlowchartEditorMode;
}

export function applyFlowchartEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  options: ApplyFlowchartEdgeChangesOptions
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();

  const allEdgeChanges = processEdgeChangesWithMirroring(edgeChanges, draft, options.editorMode);
  const validatedEdgeChanges = validateEdgeChanges(allEdgeChanges, draft.staticFlowchart.edgesStaticData);

  // Process ADDs before REMOVEs so that when edges are restructured, new edges
  // exist when the old edge's removal triggers orphan detection.
  // If a future ADD needs to check an updated list of edges including removals,
  // this ordering may need to be revisited.
  const sortedEdgeChanges = [...validatedEdgeChanges].sort((a, b) => {
    const order = {
      [FlowchartEdgeChangeType.ADD]: 0,
      [FlowchartEdgeChangeType.SELECT]: 1,
      [FlowchartEdgeChangeType.LABEL]: 1,
      [FlowchartEdgeChangeType.RESET]: 1,
      [FlowchartEdgeChangeType.REMOVE]: 2,
    };
    return (order[a.type] ?? 1) - (order[b.type] ?? 1);
  });

  sortedEdgeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartEdgeChangeType.ADD:
        if (change.item != null) {
          applyAddEdgeChange(change.item, draft, ptdvPairToTransitionIdIndex, sideEffects, options);
        }
        break;
      case FlowchartEdgeChangeType.SELECT:
        if (change.selected) {
          draft.metadata.interactionState.selectedEdgeIds.add(change.id);
        } else {
          draft.metadata.interactionState.selectedEdgeIds.delete(change.id);
        }
        break;
      case FlowchartEdgeChangeType.REMOVE:
        applyRemoveEdgeChange(
          change.id,
          draft,
          ptdvPairToTransitionIdIndex,
          sideEffects,
          options,
          change.deletedNodeContext
        );
        break;
      case FlowchartEdgeChangeType.LABEL: {
        if (change.label != null) {
          draft.metadata.interactionState.edgeLabelsByEdgeId[change.id] = change.label;
        } else {
          delete draft.metadata.interactionState.edgeLabelsByEdgeId[change.id];
        }
        const targetEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === change.id);
        if (targetEdge == null) {
          // Edge may have been removed in a prior batch (e.g., node deletion removes all
          // outbound edges, and label-clearing side effects arrive in the next iteration).
          assert(change.label == null, 'Target edge should not be null when setting a label');
          break;
        }
        targetEdge.label = change.label;
        break;
      }
      case FlowchartEdgeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected edge change type', {change});
    }
  });

  return sideEffects;
}
